import { isOrderForToday } from "./dateUtils";

const TERMINAL = new Set([
  "delivered",
  "failed",
  "cancelled",
  "canceled",
  "completed",
  "refunded",
  "returned",
]);

/**
 * Statuses that mean the order is still at the store / not yet picked up.
 * API may use different casing or synonyms; we normalize with toLowerCase.
 */
/** Shown in basket while still at store (includes not-yet-ready). */
const BASKET_PICKUP_STATUSES = new Set([
  "ready",
  "partner_assigned",
  "accepted",
  "preparing",
  "prepared",
  "packed",
  "confirmed",
  "scheduled",
  "pending",
]);

/** Must match POST /delivery/orders/:id/pickup/ (ready | partner_assigned | out_for_delivery). */
const BASKET_CONFIRM_PICKUP_STATUSES = new Set([
  "ready",
  "partner_assigned",
  "out_for_delivery",
]);

export function getOrderBasketStatus(
  order: Record<string, unknown>
): string {
  return String(order.status ?? "")
    .toLowerCase()
    .trim();
}

export function isOrderConfirmableInBasket(
  order: Record<string, unknown>
): boolean {
  return BASKET_CONFIRM_PICKUP_STATUSES.has(getOrderBasketStatus(order));
}

/** Lower = higher in basket list (ready first, preparing last). */
export function basketDisplaySortPriority(
  order: Record<string, unknown>
): number {
  const status = getOrderBasketStatus(order);
  switch (status) {
    case "ready":
      return 0;
    case "partner_assigned":
      return 1;
    case "out_for_delivery":
      return 2;
    case "preparing":
      return 100;
    case "confirmed":
    case "scheduled":
    case "pending":
      return 60;
    case "prepared":
    case "packed":
    case "accepted":
      return 40;
    default:
      return isOrderConfirmableInBasket(order) ? 10 : 70;
  }
}

export function sortBasketOrdersForDisplay<T>(
  items: T[],
  toOrderRecord: (item: T) => Record<string, unknown> = (item) =>
    item as Record<string, unknown>
): T[] {
  return [...items].sort(
    (a, b) =>
      basketDisplaySortPriority(toOrderRecord(a)) -
      basketDisplaySortPriority(toOrderRecord(b))
  );
}

/**
 * Basket = verify load before "Today's" run. Same GET /delivery/orders/today/.
 * - Preparing / confirmed: visible only; confirm requires ready or partner_assigned (API).
 * - `out_for_delivery`: shown until partner confirms (see basketReleaseStorage).
 */
export function isOrderEligibleForBasket(
  order: Record<string, unknown>,
  releasedToTodayIds: Set<number>
): boolean {
  if (!isOrderForToday(order)) return false;
  const id = Number(order.id);
  if (!Number.isFinite(id)) return false;
  const status = String(order.status ?? "")
    .toLowerCase()
    .trim();
  if (!status) return false;
  if (TERMINAL.has(status)) return false;
  if (releasedToTodayIds.has(id)) return false;
  if (status === "out_for_delivery") return true;
  return BASKET_PICKUP_STATUSES.has(status);
}
